import { Edge } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { ListResponse as OverridesListResponse } from '@weave/schema-gen-ts/dist/schemas/phone/override/v2/override_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { i18next } from '@frontend/i18n';
import { NodeState, NodeStateWithAdditionalData } from '../../call-route-details/add-step-panel/types';
import { NodeAction, NodeActionType } from '../../types';
import { logError } from '../utils';
import {
  getPhoneTreeOverrideUsage,
  hasDownStreamSteps,
  isAssociativeNode,
  isTerminalNode,
  willRemoveDownstream,
} from './node-utils';

/**
 * Core logic for determining if a warning should be shown when editing a node.
 * This helper function is used by both NodeStateWithAdditionalData and Node-based checks.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node being edited.
 * @param {string} params.currentNodeType - The current type of the node.
 * @param {string} params.newNodeType - The new type of the node.
 * @param {boolean} params.newTypeIsAssociative - Whether the new node type is associative.
 * @param {boolean} params.newNodeIsTerminal - Whether the new node is terminal.
 * @param {Node[]} params.nodes - The list of all nodes.
 * @param {Edge[]} params.edges - The list of all edges.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 */
export const shouldShowWarningForEditCore = ({
  nodeId,
  currentNodeType,
  newNodeType,
  newTypeIsAssociative,
  newNodeIsTerminal,
  nodes,
  edges,
}: {
  nodeId: string;
  currentNodeType: string;
  newNodeType: string;
  newTypeIsAssociative: boolean;
  newNodeIsTerminal: boolean;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  // If the node type has not changed, then skip the logic to show a warning unless the type is a forwarding number
  // since the forwarding number node could have changed it's ring duration to "ring until answered" which needs to
  // be considered. It would be considered a terminal node in this case (isTerminalNode function returns true in
  // this case)
  if (currentNodeType === newNodeType && !(newNodeType === CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER)) {
    return false;
  }

  const isAssociative = isAssociativeNode(currentNodeType);
  const hasChildrenSteps = hasDownStreamSteps({ nodeId: nodeId, nodes, edges });

  if (!isAssociative && hasChildrenSteps && newTypeIsAssociative) {
    return true;
  }

  if (isAssociative && hasChildrenSteps && !newTypeIsAssociative) {
    return true;
  }

  if (isAssociative && hasChildrenSteps && newTypeIsAssociative) {
    return true;
  }

  if (!isAssociative && hasChildrenSteps && !newTypeIsAssociative && newNodeIsTerminal) {
    return true;
  }

  return false;
};

/**
 * Determines whether a warning should be shown when editing a node.
 * The states to show a warning are:
 *
 * - non-associative step w/ children --> associative step
 *    The user is trying to change a non-associative node to an associative node and the non-associative node has
 *    downstream steps (grandchildren in this case since the direct children are meta nodes such as dialOptions,
 *    "fallback", "open", "closed" and "break" nodes).
 *
 * - associative step w/ children --> non-associative step
 *    The user is trying to change an associative node to a non-associative node and the associative node has downstream steps.
 *
 * - associative step w/ children --> associative step
 *    The user is trying to change an associative node to another associative node and the original associative node has downstream steps.
 *
 * - non-associative step w/ children --> non-associative step and is terminal
 *    The user is trying to change a non-associative node to another non-associative node but the new step is a terminal step
 *    and the original non-associative node has downstream steps
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node being edited.
 * @param {NodeStateWithAdditionalData} params.newNodeData - The new editing data of the node.
 * @param {Node[]} params.nodes - The list of all nodes.
 * @param {Edge[]} params.edges - The list of all edges.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 * ```
 */
export const shouldShowWarningForEdit = ({
  nodeId,
  newNodeData,
  nodes,
  edges,
}: {
  nodeId: string;
  newNodeData: NodeStateWithAdditionalData;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  const editingNode = nodes.find((node) => node.id === nodeId);
  if (!editingNode) {
    logError('Could not find editing node in shouldShowWarningForEdit: PC-8djf032l', 'shouldShowWarningForEdit', {
      nodeId,
      nodes: nodes.map((n) => n.id),
    });
    return false;
  }

  const newNodeType = newNodeData.nodeState.type;
  const newTypeIsAssociative = isAssociativeNode(newNodeType);
  const newNodeIsTerminal = isTerminalNode(newNodeData.nodeState);

  return shouldShowWarningForEditCore({
    nodeId,
    currentNodeType: editingNode.type,
    newNodeType,
    newTypeIsAssociative,
    newNodeIsTerminal,
    nodes,
    edges,
  });
};

/**
 * Determines whether a warning should be shown when replacing a node with a duplicate (from phone tree duplication).
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node being replaced.
 * @param {Node} params.duplicateRootNode - The root node of the duplicate subgraph that will replace the existing node.
 * @param {Node[]} params.nodes - The list of all nodes.
 * @param {Edge[]} params.edges - The list of all edges.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 */
export const shouldShowWarningForEditDuplicate = ({
  nodeId,
  duplicateRootNode,
  nodes,
  edges,
}: {
  nodeId: string;
  duplicateRootNode: CallRouteV1.Types.Node;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  const editingNode = nodes.find((node) => node.id === nodeId);
  if (!editingNode) {
    logError(
      'Could not find editing node in shouldShowWarningForEditDuplicate: PC-8djf032l2',
      'shouldShowWarningForEditDuplicate',
      {
        nodeId,
        nodes: nodes.map((n) => n.id),
      }
    );
    return false;
  }

  const newNodeType = duplicateRootNode.type;
  const newTypeIsAssociative = isAssociativeNode(newNodeType);
  // Duplicate nodes are always based on existing nodes, so we check if it's terminal based on the node structure
  const newNodeIsTerminal = !newTypeIsAssociative; // Simplified check - can be enhanced if needed

  return shouldShowWarningForEditCore({
    nodeId,
    currentNodeType: editingNode.type,
    newNodeType,
    newTypeIsAssociative,
    newNodeIsTerminal,
    nodes,
    edges,
  });
};

/**
 * Core logic for determining if a warning should be shown when adding a node.
 * This helper function is used by both NodeStateWithAdditionalData and Node-based checks.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.extendingNodeId - The ID of the node being extended.
 * @param {boolean} params.newTypeIsAssociative - Whether the new node type is associative.
 * @param {boolean} params.newNodeIsTerminal - Whether the new node is terminal.
 * @param {Node[]} params.nodes - The list of all nodes in the call routes.
 * @param {Edge[]} params.edges - The list of all edges in the call routes.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 */
export const shouldShowWarningForAddCore = ({
  extendingNodeId,
  newTypeIsAssociative,
  newNodeIsTerminal,
  nodes,
  edges,
}: {
  extendingNodeId: string;
  newTypeIsAssociative: boolean;
  newNodeIsTerminal: boolean;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  const hasChildrenSteps = hasDownStreamSteps({ nodeId: extendingNodeId, nodes, edges });

  if (hasChildrenSteps && (newTypeIsAssociative || newNodeIsTerminal)) {
    return true;
  }

  return false;
};

/**
 * Determines whether a warning should be shown when adding a new node to the call routes.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.extendingNodeId - The ID of the node being extended.
 * @param {NodeStateWithAdditionalData} params.newNodeData - The state of the new node being added.
 * @param {Node[]} params.nodes - The list of all nodes in the call routes.
 * @param {Edge[]} params.edges - The list of all edges in the call routes.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 */
export const shouldShowWarningForAdd = ({
  extendingNodeId,
  newNodeData,
  nodes,
  edges,
}: {
  extendingNodeId: string;
  newNodeData: NodeStateWithAdditionalData;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  const extendingNode = nodes.find((node) => node.id === extendingNodeId);
  if (!extendingNode) {
    logError('Could not find extending node in shouldShowWarningForAdd: PC-9ei3kfg', 'shouldShowWarningForAdd', {
      extendingNodeId,
      nodes: nodes.map((n) => n.id),
    });
    return false;
  }

  const newNodeType = newNodeData.nodeState.type;
  const newTypeIsAssociative = isAssociativeNode(newNodeType);
  const newNodeIsTerminal = isTerminalNode(newNodeData.nodeState);

  return shouldShowWarningForAddCore({
    extendingNodeId,
    newTypeIsAssociative,
    newNodeIsTerminal,
    nodes,
    edges,
  });
};

/**
 * Determines whether a warning should be shown when adding a duplicate node (from phone tree duplication).
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.extendingNodeId - The ID of the node being extended.
 * @param {Node} params.duplicateRootNode - The root node of the duplicate subgraph being added.
 * @param {Node[]} params.nodes - The list of all nodes in the call routes.
 * @param {Edge[]} params.edges - The list of all edges in the call routes.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 */
export const shouldShowWarningForAddDuplicate = ({
  extendingNodeId,
  duplicateRootNode,
  nodes,
  edges,
}: {
  extendingNodeId: string;
  duplicateRootNode: CallRouteV1.Types.Node;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  const extendingNode = nodes.find((node) => node.id === extendingNodeId);
  if (!extendingNode) {
    logError(
      'Could not find extending node in shouldShowWarningForAddDuplicate: PC-9ei3kfg2',
      'shouldShowWarningForAddDuplicate',
      {
        extendingNodeId,
        nodes: nodes.map((n) => n.id),
      }
    );
    return false;
  }

  const newTypeIsAssociative = isAssociativeNode(duplicateRootNode.type);
  const newNodeIsTerminal = isTerminalNode(duplicateRootNode);

  return shouldShowWarningForAddCore({
    extendingNodeId,
    newTypeIsAssociative,
    newNodeIsTerminal,
    nodes,
    edges,
  });
};

/**
 * Determines whether a warning should be shown when deleting a node.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.deletingNodeId - The ID of the node being deleted.
 * @param {Node[]} params.nodes - The list of all nodes.
 * @param {Edge[]} params.edges - The list of all edges.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 */
export const shouldShowWarningForDelete = ({
  deletingNodeId,
  nodes,
  edges,
}: {
  deletingNodeId: string;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
}): boolean => {
  const deletingNode = nodes.find((node) => node.id === deletingNodeId);
  if (!deletingNode) {
    logError('Could not find deleting node: PC-5mf83k', 'shouldShowWarningForDelete', {
      deletingNodeId,
      nodes: nodes.map((n) => n.id),
    });
    return false;
  }

  const withDownStreamSteps = hasDownStreamSteps({ nodeId: deletingNodeId, nodes, edges });
  const isAssociative = isAssociativeNode(deletingNode.type);

  if (isAssociative && withDownStreamSteps) {
    return true;
  } else if (deletingNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS && withDownStreamSteps) {
    return true;
  }

  return false;
};

/**
 * Determines whether a warning should be shown based on the provided action type and node details.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node being acted upon.
 * @param {NodeTypes} [params.newNodeData] - The new data of the node, if applicable (the updated data when editing a node or the data when adding a new node).
 * @param {Node[]} params.nodes - The list of all nodes.
 * @param {Edge[]} params.edges - The list of all edges.
 * @param {NodeActionType} params.actionType - The type of action being performed on the node.
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 */
export const shouldShowWarning = ({
  nodeId,
  newNodeData,
  nodes,
  edges,
  actionType,
}: {
  nodeId: string;
  newNodeData?: NodeStateWithAdditionalData;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  actionType: NodeActionType;
}): boolean => {
  if (actionType === NodeAction.Edit && newNodeData) {
    return shouldShowWarningForEdit({ nodeId: nodeId, newNodeData, nodes, edges });
  } else if (actionType === NodeAction.Add && newNodeData) {
    return shouldShowWarningForAdd({ extendingNodeId: nodeId, newNodeData, nodes, edges });
  } else if (actionType === NodeAction.Delete) {
    return shouldShowWarningForDelete({ deletingNodeId: nodeId, nodes, edges });
  } else {
    logError('Invalid action type or missing new node data: PC-s9d0f4', 'shouldShowWarning', {
      nodeId,
      actionType,
    });
    return false;
  }
};

/**
 * Determines whether a warning should be shown when duplicating a phone tree subgraph.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node being acted upon (extending node for add, or node being replaced for edit).
 * @param {Node} params.duplicateRootNode - The root node of the duplicate subgraph.
 * @param {Node[]} params.nodes - The list of all nodes.
 * @param {Edge[]} params.edges - The list of all edges.
 * @param {NodeActionType} params.actionType - The type of action being performed (Add or Edit).
 * @returns {boolean} - Returns `true` if a warning should be shown, otherwise `false`.
 */
export const shouldShowWarningForDuplicate = ({
  nodeId,
  duplicateRootNode,
  nodes,
  edges,
  actionType,
}: {
  nodeId: string;
  duplicateRootNode: CallRouteV1.Types.Node;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  actionType: NodeActionType;
}): boolean => {
  if (actionType === NodeAction.Edit) {
    return shouldShowWarningForEditDuplicate({ nodeId, duplicateRootNode, nodes, edges });
  } else if (actionType === NodeAction.Add) {
    return shouldShowWarningForAddDuplicate({ extendingNodeId: nodeId, duplicateRootNode, nodes, edges });
  } else {
    logError('Invalid action type for duplicate: PC-s9d0f5', 'shouldShowWarningForDuplicate', {
      nodeId,
      actionType,
    });
    return false;
  }
};

/**
 * Determines whether an add or edit action should be blocked because it would remove downstream phone tree
 * nodes that are currently used in overrides.
 *
 * This is a hard block — the user cannot proceed with the action until the phone tree is removed from the
 * override(s). Uses `willRemoveDownstream` to first check if downstream nodes would be removed, then checks
 * if any of those downstream nodes are phone trees used in overrides.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node being acted upon.
 * @param {NodeState} params.newNodeState - The state of the new or updated node.
 * @param {Node[]} params.nodes - The array of all nodes.
 * @param {Edge[]} params.edges - The array of all edges.
 * @param {NodeActionType} params.actionType - The type of action being performed (Add or Edit).
 * @param {OverridesListResponse} params.overrides - The overrides data to check against.
 * @returns {boolean} - Returns `true` if the action should be blocked, otherwise `false`.
 */
export const shouldShowPhoneTreeOverrideBlocker = ({
  nodeId,
  newNodeState,
  nodes,
  edges,
  actionType,
  overrides,
}: {
  nodeId: string;
  newNodeState: NodeState;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  actionType: NodeActionType;
  overrides: OverridesListResponse;
}): boolean => {
  if (!willRemoveDownstream({ nodeId, newNodeState, nodes, edges, actionType })) {
    return false;
  }

  const phoneTreeNodeOverrides = getPhoneTreeOverrideUsage({
    nodeId,
    nodes,
    edges,
    overrides,
  });

  // True if there is at least one downstream phone tree node that is used in an override.
  return Object.keys(phoneTreeNodeOverrides).length > 0;
};

/**
 * Determines whether a duplicate action should be blocked because it would remove downstream phone tree
 * nodes that are currently used in overrides.
 * NOTE: did not use shouldShowPhoneTreeOverrideBlocker because that function requires newNodeState: NodeState which
 * is not available when duplicating since we have a whole subgraph of nodes being added/edited.
 *
 * Uses the same downstream removal logic as shouldShowWarningForDuplicate to first check if
 * downstream nodes would be removed, then checks if any of those downstream nodes are phone trees used in overrides.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.nodeId - The ID of the node being acted upon (extending node for add, or node being replaced for edit).
 * @param {Node} params.duplicateRootNode - The root node of the duplicate subgraph.
 * @param {Node[]} params.nodes - The array of all nodes.
 * @param {Edge[]} params.edges - The array of all edges.
 * @param {NodeActionType} params.actionType - The type of action being performed (Add or Edit).
 * @param {OverridesListResponse} params.overrides - The overrides data to check against.
 * @returns {boolean} - Returns `true` if the action should be blocked, otherwise `false`.
 */
export const shouldShowPhoneTreeOverrideBlockerForDuplicate = ({
  nodeId,
  duplicateRootNode,
  nodes,
  edges,
  actionType,
  overrides,
}: {
  nodeId: string;
  duplicateRootNode: CallRouteV1.Types.Node;
  nodes: CallRouteV1.Types.Node[];
  edges: Edge[];
  actionType: NodeActionType;
  overrides: OverridesListResponse;
}): boolean => {
  // First check if the duplicate action would actually remove downstream nodes.
  // If not, there's no risk of removing phone trees with overrides.
  const wouldRemoveDownstream = shouldShowWarningForDuplicate({
    nodeId,
    duplicateRootNode,
    nodes,
    edges,
    actionType,
  });

  if (!wouldRemoveDownstream) {
    return false;
  }

  const phoneTreeNodeOverrides = getPhoneTreeOverrideUsage({
    nodeId,
    nodes,
    edges,
    overrides,
  });

  // True if there is at least one downstream phone tree node that is used in an override.
  return Object.keys(phoneTreeNodeOverrides).length > 0;
};

/**
 * Determines whether a placeholder warning should be shown for a node based on its context in the call route graph.
 *
 * Currently, a placeholder warning is shown for a fallback node when its parent is a Weave Assistant node
 * and the fallback node has no children (it's a leaf node).
 *
 * @param node - The node to evaluate.
 * @param nodes - The list of all nodes in the call route.
 * @param edges - The list of all edges in the call route.
 * @returns A warning message string if a placeholder warning applies, or `undefined` otherwise.
 */
export const getNodePlaceholderWarning = (
  node: CallRouteV1.Types.Node,
  nodes: CallRouteV1.Types.Node[],
  edges: Edge[]
): string | undefined => {
  if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK) {
    const parentEdge = edges.find((edge) => edge.targetId === node.id);
    const parentNode = parentEdge ? nodes.find((n) => n.id === parentEdge.sourceId) : undefined;
    const hasChildren = edges.some((edge) => edge.sourceId === node.id);

    if (parentNode?.type === CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT && !hasChildren) {
      return 'Add a fallback step to prevent the call from disconnecting if the AI Receptionist is unable to assist the caller.';
    }
  }

  return undefined;
};

/**
 * Takes a node object and runs certain checks on it to determine if any warnings should be displayed.
 *
 * @param node - The node object containing the call object to evaluate.
 * @returns A localized warning message if conditions are met, or an empty string otherwise.
 */
export const getNodeWarning = (node: CallRouteV1.Types.Node) => {
  if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP) {
    if ((node.callObject.callGroupExpansion.deviceNames?.length ?? 0) < 1) {
      return i18next.t('0 Devices in Call Group', { ns: 'phone' });
    }
  }

  if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE) {
    if ((node.callObject.callQueueExpansion.deviceNames?.length ?? 0) < 1) {
      return i18next.t('0 Devices in Call Queue', { ns: 'phone' });
    }
  }

  if (node.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE) {
    if (!node.callObject.phoneTreeExpansion.promptMediaItemId) {
      return i18next.t('Needs audio prompt', { ns: 'phone' });
    }
  }

  return '';
};
